// @givmo/board — the pieces a director's signed-in steps share: the invitation's (CBG-SEC2) and the
// seat move's (CBG-SEC2-L4-MOVE, D-CBG-23 flow (b)).
//
// The move's code step is "the invitation's code step, word for word, with the move on its button"
// (the approved frame b5), and its wrong code the invitation's own line (b5w), so the two pages draw
// the same card, code boxes, resend row and "Use another account" link. Each page keeps its own test
// ids: the invitation's are the e2e suite's, unchanged.

import { useEffect, useState, type ReactNode } from 'react';
import { Button, Card, CardBody, ErrorMessage, OtpField, testIdProp } from '@givmo/design-system';

/** The house text link (`auth-door.tsx`, `sign-in-screen.tsx`). */
export const STEP_LINK =
  'font-sans font-w600 tracking-givmo text-brand-700 outline-none transition-colors hover:text-brand-800 focus-visible:underline';
/** A resend link while it cools down: the same link, disabled, in the quiet grey. */
const COOLING =
  'cursor-default font-sans text-s13 font-w600 tracking-givmo text-neutral-400 outline-none';
export const STEP_BODY =
  'font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary';
/** A new code can be asked for this long after the last (the console's own resend wait). */
const RESEND_SECONDS = 30;

/** The one card every signed-in step uses: the ways-in card's width, so nothing jumps. */
export function StepCard({ children, testId }: { children: ReactNode; testId: string }) {
  return (
    <Card className="w-full max-w-[440px]" testId={testId}>
      <CardBody className="flex flex-col gap-16">{children}</CardBody>
    </Card>
  );
}

/** "Use another account": signs this account out, back to the page's ways in. */
export function UseAnotherAccount({ onClick, testId }: { onClick: () => void; testId: string }) {
  return (
    <button
      type="button"
      className={`${STEP_LINK} self-center text-s13`}
      onClick={onClick}
      {...testIdProp(testId)}
    >
      Use another account
    </button>
  );
}

/** Seconds until a new code may be asked for, counting down once a second. */
function useResendWait(sentAt: number): number {
  const remaining = () =>
    sentAt === 0 ? 0 : Math.max(0, RESEND_SECONDS - Math.floor((Date.now() - sentAt) / 1000));
  const [seconds, setSeconds] = useState(remaining);
  useEffect(() => {
    if (seconds <= 0) return;
    const timer = window.setTimeout(() => setSeconds(remaining()), 1000);
    return () => window.clearTimeout(timer);
    // `remaining` reads `sentAt`, which keys this component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seconds]);
  return seconds;
}

export interface CodeStepProps {
  masked: string;
  /** How many codes have been refused since this one was sent. */
  failures: number;
  sentAt: number;
  submitting: boolean;
  /** The code cap is reached: no new code can be asked for, so its line stands in the link's place. */
  throttled: boolean;
  /** The button's words: the act the code finishes ("Join the board", "Move my seat"). */
  action: string;
  /** The page's own test ids: `<prefix>-code-step`, `-code`, `-code-wrong`, `-code-join`, … */
  testIdPrefix: string;
  /**
   * CBG-FIX161: Board's sentence for an act that didn't go through though the code is still good
   * (it couldn't link the seat just now). Said under the boxes, which keep the code, so pressing the
   * act again sends the same code. `null` or absent: nothing to say.
   */
  notice?: string | null;
  onSubmit: (code: string) => void;
  onResend: () => void;
  onUseAnotherAccount: () => void;
}

export function CodeStep({
  masked,
  failures,
  sentAt,
  submitting,
  throttled,
  action,
  testIdPrefix,
  notice = null,
  onSubmit,
  onResend,
  onUseAnotherAccount,
}: CodeStepProps) {
  const [code, setCode] = useState('');
  const wait = useResendWait(sentAt);
  const wrong = failures > 0;
  // EVERY wrong, expired, spent or exhausted code clears the boxes, and the newest code is the one to
  // try. Keyed on the count, so the second refusal clears them as the first did.
  useEffect(() => {
    if (failures > 0) setCode('');
  }, [failures]);

  return (
    <StepCard testId={`${testIdPrefix}-code-step`}>
      {/* The code mail says how long it lasts; a wrong or expired one is said below. */}
      <p className={STEP_BODY}>Enter the 6-digit code we sent to {masked}.</p>
      <div className="flex flex-col">
        <OtpField
          value={code}
          onValueChange={setCode}
          error={wrong && code.length === 0}
          aria-label="6-digit code"
          testId={`${testIdPrefix}-code`}
        />
        {wrong && code.length === 0 ? (
          <ErrorMessage testId={`${testIdPrefix}-code-wrong`}>
            That code didn’t work. Try the newest one we sent.
          </ErrorMessage>
        ) : notice !== null ? (
          <ErrorMessage testId={`${testIdPrefix}-code-notice`}>{notice}</ErrorMessage>
        ) : null}
      </div>
      <Button
        appearance="saas"
        tone="primary"
        size="lg"
        fullWidth
        loading={submitting}
        disabled={code.length !== 6}
        onClick={() => onSubmit(code)}
        testId={`${testIdPrefix}-code-join`}
      >
        {action}
      </Button>
      {/* One row, one treatment: both are links. While the resend cools down it is the quiet,
          disabled grey; once it can send, it is the same blue link as "Use another account". Once
          the cap is reached there is no new code to ask for, and the cap's line stands there. */}
      <div className="flex items-center justify-between gap-12 border-t border-border-subtle pt-16">
        {throttled ? (
          <p
            className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
            {...testIdProp(`${testIdPrefix}-code-throttled`)}
          >
            You’ve asked for several codes. Use the newest one we sent, or try again later.
          </p>
        ) : wait > 0 ? (
          <button
            type="button"
            disabled
            className={COOLING}
            {...testIdProp(`${testIdPrefix}-resend`)}
          >
            Send a new code in <span className="tabular-nums">{wait}</span>s
          </button>
        ) : (
          <button
            type="button"
            className={`${STEP_LINK} text-s13`}
            onClick={onResend}
            {...testIdProp(`${testIdPrefix}-resend`)}
          >
            Send a new code
          </button>
        )}
        <button
          type="button"
          className={`${STEP_LINK} text-s13`}
          onClick={onUseAnotherAccount}
          {...testIdProp(`${testIdPrefix}-use-another-account`)}
        >
          Use another account
        </button>
      </div>
    </StepCard>
  );
}
